import { EntityType, useAppSelector } from '@frontend/common';
import { CheckboxInput, SelectInputV2 } from '@frontend/components/basic-forms';
import { PaginationButtonNavigation, THeader } from '@frontend/components/list';
import { ModalType, useModals } from '@frontend/modal';
import { ListInfo, ResolveFunction, TableAutoRefreshMode, changePageSize, changeSorting, listStore } from '@frontend/repository';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import type { Column } from '@tanstack/react-table';
import { ColumnDef, Table as ReactTable, Row, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import React, { JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { getColumnOptionsLabel } from './get-column-options-label';
import { ReactiveRowProps, ReactiveTRow } from './rows/reactive-row.component';
import { useFillAvailableHeight } from './use-fill-available-height';
import { useTableViewPreferences } from './use-table-view-preferences';

export interface ReactiveTableProps {
    dispatch: ThunkDispatch<any, any, Action>;
    listId: string;
    /** Entity type behind the rows; each row subscribes to its own topic for this type. */
    entityType: EntityType;
    useStaticListId?: boolean;
    headerClassName?: string;
    headerItemClassName?: string;
    rowClassName?: string;
    customDivclassName?: string;
    customRowClassName?: (row: Row<object>) => string | undefined;

    selectEnabled?: boolean;
    expandEnabled?: boolean;
    selectedItemsCallback?: (objects: any[]) => void;
    columns: ColumnDef<any>[];
    data: any[];
    autoResetPage?: boolean;
    next?: string | null | undefined;
    count?: number | undefined;
    onSelectElement?: (object: any) => void;
    RowElement: (props: ReactiveRowProps) => JSX.Element;
    renderSubComponent?: (props: { row: any }) => React.ReactElement;
    /** Overrides the resolver picked by entity type; see `useReactiveRow`. */
    resolveFunction?: ResolveFunction;
    /** Called when a row update cannot be resolved, so the list can prompt a manual refresh. */
    onRowFallback?: () => void;
    pagination?: {
        page?: (value: number) => void;
        initialPageSize?: number;
        hidePagination?: boolean;
        hideResults?: boolean;
        hidePageSize?: boolean;

        allowPageSizeSelect?: boolean;
        allowPageJumpInput?: { bottom?: boolean; top?: boolean };
        allowPageButtonNavigation?: {
            bottom?: boolean;
            bottomProps?: Partial<Omit<React.ComponentProps<typeof PaginationButtonNavigation>, 'dispatch' | 'listStoreId'>>;
            top?: boolean;
            topProps?: Partial<Omit<React.ComponentProps<typeof PaginationButtonNavigation>, 'dispatch' | 'listStoreId'>>;
        };
    };
    id?: string;
    hideColumns?: string[];
    sortingColumns?: string[];
    /**
     * @description Columns where the sort direction should be reversed when sent to the backend.
     * This is useful for computed values where ascending/descending needs to be inverted.
     */
    reverseSortingColumns?: string[];
    showCount?: boolean;
    includeDeletedCallback?: (value: boolean) => void;
    optionsModalOpen?: boolean;
    setOptionsModalOpen?: (value: boolean) => void;
    customFooter?: React.ReactNode | ((table: ReactTable<any>) => React.ReactNode);
    maxHeight?: boolean;
    defaultHeight?: boolean;
    hideFooter?: boolean;
    hideFilter?: boolean;
    isLoading?: boolean;
    /** True while the list is refetching with existing data visible (search, filter, pagination). */
    isUpdating?: boolean;
    /** Stable frontend identifier used for persisted per-user table view settings. */
    tableViewId?: string;
    /** Enables column visibility/order controls and persisted table view preferences. */
    tableOptionsEnabled?: boolean;
    /** Receives whether the visible column order differs from the table default. */
    onTableViewCustomizationChange?: (isCustomized: boolean) => void;
    /** Receives the persisted create-refresh preference whenever it loads or changes. */
    onAutoRefreshModeChange?: (mode: TableAutoRefreshMode) => void;
}

export function ReactiveTable(props: ReactiveTableProps) {
    const store = useAppSelector(useSelector, listStore);
    const list: ListInfo | undefined = store[props.listId];
    const pendingDeleteIds = list?.pendingDeleteIds ?? emptyPendingDeletes;

    const tableSorting = useMemo(() => {
        if (!list?.sorting) return [];
        if (!props.reverseSortingColumns?.length) return list.sorting;
        return list.sorting.map((sort) => (props.reverseSortingColumns!.includes(sort.id) ? { ...sort, desc: !sort.desc } : sort));
    }, [list?.sorting, props.reverseSortingColumns]);
    const possiblePageSizes = [10, 25, 50, 100, 150];
    const totalCount = list?.count ?? props.count ?? props.data.length;
    const pageSize = list?.pageSize ?? props.pagination?.initialPageSize ?? 25;
    const pageCount = totalCount ? Math.ceil(totalCount / pageSize) : 0;
    const { openModal, closeModal, updateModal } = useModals();

    const defaultColumnIds = useMemo(() => props.columns.map(getColumnId).filter((id): id is string => Boolean(id)), [props.columns]);
    const tableViewId = props.tableOptionsEnabled ? props.tableViewId : undefined;
    const {
        columnVisibility,
        setColumnVisibility,
        columnOrder,
        setColumnOrder,
        visibleColumnIds,
        autoRefresh,
        setAutoRefresh,
        isTableViewCustomized,
        resetTableView
    } = useTableViewPreferences({
        tableViewId,
        defaultColumnIds,
        hideColumns: props.hideColumns
    });
    const columnLayoutKey = useMemo(() => visibleColumnIds.join('\u001f'), [visibleColumnIds]);

    const optionsModalIdRef = useRef<string | null>(null);

    useEffect(() => {
        props.onTableViewCustomizationChange?.(isTableViewCustomized);
    }, [isTableViewCustomized, props.onTableViewCustomizationChange]);

    useEffect(() => {
        if (!props.tableOptionsEnabled) return;
        props.onAutoRefreshModeChange?.(autoRefresh);
    }, [autoRefresh, props.onAutoRefreshModeChange, props.tableOptionsEnabled]);

    useEffect(() => {
        if (props.pagination?.initialPageSize !== undefined && !possiblePageSizes.includes(props.pagination.initialPageSize)) {
            possiblePageSizes.push(props.pagination.initialPageSize);
            possiblePageSizes.sort((a, b) => a - b);
            props.dispatch(changePageSize({ listId: props.listId, pageSize: props.pagination.initialPageSize }));
        }
    }, [props.pagination?.initialPageSize, props.dispatch, props.listId]);

    const tableInstance = useReactTable({
        columns: props.columns,
        data: props.data,
        autoResetPageIndex: props.autoResetPage ? props.autoResetPage : false,
        manualPagination: true,
        pageCount: pageCount,
        manualSorting: true,
        autoResetExpanded: false,
        getSubRows: (row: any) => row?.children,
        // Key rows by entity so patching one row never remounts the others.
        getRowId: (row: any, index: number, parent?: Row<any>) => (row?.id != null ? String(row.id) : parent ? `${parent.id}.${index}` : String(index)),
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        state: {
            columnVisibility,
            columnOrder,
            sorting: tableSorting
        },
        onSortingChange: (updater) => {
            const newSorting = typeof updater === 'function' ? updater(tableSorting) : updater;
            if (!props.reverseSortingColumns?.length) {
                props.dispatch(changeSorting({ listId: props.listId, sorting: newSorting }));
                return;
            }
            const adjustedSorting = newSorting.map((sort) => (props.reverseSortingColumns!.includes(sort.id) ? { ...sort, desc: !sort.desc } : sort));
            props.dispatch(changeSorting({ listId: props.listId, sorting: adjustedSorting }));
        },
        onColumnVisibilityChange: setColumnVisibility,
        onColumnOrderChange: setColumnOrder
    });

    useEffect(() => {
        if (props.selectEnabled && props.selectedItemsCallback) {
            props.selectedItemsCallback(tableInstance.getSelectedRowModel().flatRows.map((r) => r.original));
        }
    }, [props.selectEnabled, props.selectedItemsCallback, tableInstance]);

    const hidePagination = props.pagination?.hidePagination ?? totalCount <= pageSize;
    const { ref: fillHeightRef, height: fillHeight } = useFillAvailableHeight(!props.defaultHeight);

    const memoizedModalRenderFunction = useCallback(() => {
        return (
            <ReactiveTableOptionsModal
                onReset={resetTableView}
                columns={getOrderedColumns(tableInstance.getAllLeafColumns(), columnOrder)}
                onReorderColumn={(draggedColumnId, targetColumnId) => setColumnOrder((current) => reorderColumn(current, draggedColumnId, targetColumnId))}
                visibleColumnCount={visibleColumnIds.length}
                autoRefresh={autoRefresh}
                onAutoRefreshChange={setAutoRefresh}
            />
        );
    }, [autoRefresh, columnOrder, resetTableView, setAutoRefresh, setColumnOrder, tableInstance, visibleColumnIds.length]);

    useEffect(() => {
        if (props.tableOptionsEnabled && props.optionsModalOpen && optionsModalIdRef.current === null) {
            const id = openModal(
                'table-options-modal',
                memoizedModalRenderFunction,
                { handleCloseCallback: () => props.setOptionsModalOpen && props.setOptionsModalOpen(false) },
                { customWidth: 20, type: ModalType.PANE, className: 'modal-without-radius' }
            );
            optionsModalIdRef.current = id;
        } else if ((!props.tableOptionsEnabled || !props.optionsModalOpen) && optionsModalIdRef.current !== null) {
            closeModal(optionsModalIdRef.current);
            optionsModalIdRef.current = null;
        }
    }, [props.optionsModalOpen, props.tableOptionsEnabled, openModal, closeModal, props.setOptionsModalOpen, memoizedModalRenderFunction]);
    useEffect(() => {
        if (props.tableOptionsEnabled && optionsModalIdRef.current !== null && props.optionsModalOpen) {
            updateModal(optionsModalIdRef.current, memoizedModalRenderFunction);
        }
    }, [
        autoRefresh,
        columnVisibility,
        columnOrder,
        optionsModalIdRef.current,
        props.optionsModalOpen,
        props.tableOptionsEnabled,
        updateModal,
        memoizedModalRenderFunction
    ]);

    const style = fillHeight === undefined ? undefined : props.maxHeight ? { maxHeight: fillHeight } : { height: fillHeight };
    const showBottomPageNavigation =
        !hidePagination &&
        pageCount > 1 &&
        (!props.pagination || !props.pagination.allowPageButtonNavigation || props.pagination.allowPageButtonNavigation.bottom !== false);

    return (
        <div
            ref={fillHeightRef}
            data-testid='table-container'
            id={props.id}
            aria-busy={props.isUpdating ? 'true' : undefined}
            style={props.defaultHeight ? undefined : style}
            className={`relative flex max-w-full flex-col justify-between ${props.defaultHeight ? 'min-h-0 flex-1 overflow-hidden' : 'overflow-auto'}`}>
            <div className='min-w-0 flex-1 overflow-x-auto'>
                <table
                    data-testid={props.id}
                    className='w-full border-collapse text-left'>
                    <thead className='outline-border-default z-20 h-12 outline-1'>
                        {tableInstance.getHeaderGroups().map((headerGroup, index) => (
                            <THeader
                                className={props.headerItemClassName}
                                customDivClassName={props.customDivclassName}
                                hideFilter={props.hideFilter}
                                key={index + '_' + headerGroup.id}
                                headerGroup={headerGroup}
                                sortingColumns={props.sortingColumns}
                                tableId={props.listId}
                                dispatch={props.dispatch}
                            />
                        ))}
                    </thead>

                    <tbody className={props.isUpdating ? 'pointer-events-none opacity-60' : undefined}>
                        {props.data.length === 0 && (
                            <tr>
                                <td
                                    className='p-3'
                                    colSpan={props.columns.length}>
                                    <div className='text-primary ms-2'>
                                        <FormattedMessage
                                            id='table.no-data'
                                            description='The label we display when there are no results in a table'
                                            defaultMessage='No data to display'
                                        />
                                    </div>
                                </td>
                            </tr>
                        )}
                        {tableInstance.getRowModel().rows.map((row) => (
                            <ReactiveTRow
                                className={props.customRowClassName ? props.customRowClassName(row) : props.rowClassName}
                                key={row.id}
                                row={row}
                                selectEnabled={props.selectEnabled}
                                onClick={props.onSelectElement}
                                RowElement={props.RowElement}
                                dispatch={props.dispatch}
                                listId={props.listId}
                                entityType={props.entityType}
                                pendingDeleteIds={pendingDeleteIds}
                                resolveFunction={props.resolveFunction}
                                onRowFallback={props.onRowFallback}
                                columnLayoutKey={columnLayoutKey}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
            {!props.hideFooter && (
                <div
                    data-testid={props.id + '-footer'}
                    className='border-border-default bg-background-surface sticky bottom-[-1px] left-0 z-5 flex w-full flex-wrap items-center justify-between gap-3 self-stretch rounded-br-2xl rounded-bl-2xl border-t p-4'>
                    {props.customFooter ? (
                        typeof props.customFooter === 'function' ? (
                            props.customFooter(tableInstance)
                        ) : (
                            props.customFooter
                        )
                    ) : (
                        <>
                            <div className='flex items-center justify-start gap-4'>
                                {!props.pagination?.hideResults && (
                                    <div className='flex items-center justify-start gap-2.5'>
                                        <div className='text-foreground-default flex justify-start gap-1 text-lg font-semibold'>
                                            <span>{totalCount}</span>
                                            <FormattedMessage
                                                id='table.results'
                                                description='The label for results'
                                                defaultMessage='results'
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                            {showBottomPageNavigation && (
                                <PaginationButtonNavigation
                                    dispatch={props.dispatch}
                                    listStoreId={props.listId}
                                    id={props.id + '-bottom'}
                                    {...props.pagination?.allowPageButtonNavigation?.bottomProps}
                                />
                            )}
                            {!props.pagination?.hidePageSize && (
                                <div className='inline-flex items-center justify-end gap-3'>
                                    <span className='text-brand-muted font-medium text-nowrap'>
                                        <FormattedMessage
                                            id='table.pageSize'
                                            description='The page size in the default table message.'
                                            defaultMessage='Page size'
                                        />
                                    </span>
                                    <SelectInputV2<number>
                                        options={possiblePageSizes.map((size) => ({ label: size.toString(), value: size.toString() }))}
                                        value={list?.pageSize?.toString()}
                                        onSelect={(value) => {
                                            props.dispatch(changePageSize({ listId: props.listId, pageSize: Number(value?.value) }));
                                        }}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

const emptyPendingDeletes: string[] = [];

const getColumnId = (column: ColumnDef<any>): string | null => {
    if (typeof column.id === 'string') return column.id;
    const accessorKey = (column as { accessorKey?: unknown }).accessorKey;
    return typeof accessorKey === 'string' ? accessorKey : null;
};

const getOrderedColumns = (columns: Column<any, unknown>[], columnOrder: string[]): Column<any, unknown>[] => {
    const columnsById = new Map(columns.map((column) => [column.id, column]));
    const orderedColumns = columnOrder.map((id) => columnsById.get(id)).filter((column): column is Column<any, unknown> => Boolean(column));
    const missingColumns = columns.filter((column) => !columnOrder.includes(column.id));
    return [...orderedColumns, ...missingColumns];
};

const reorderColumn = (columns: string[], draggedColumnId: string, targetColumnId: string): string[] => {
    if (draggedColumnId === targetColumnId) return columns;

    const nextColumns = [...columns];
    const draggedIndex = nextColumns.indexOf(draggedColumnId);
    const targetIndex = nextColumns.indexOf(targetColumnId);
    if (draggedIndex < 0 || targetIndex < 0) return columns;

    const [column] = nextColumns.splice(draggedIndex, 1);
    nextColumns.splice(targetIndex, 0, column);
    return nextColumns;
};

export const ReactiveTableOptionsModal = (props: {
    onReset: () => void;
    columns: Column<any, unknown>[];
    onReorderColumn: (draggedColumnId: string, targetColumnId: string) => void;
    visibleColumnCount: number;
    autoRefresh: TableAutoRefreshMode;
    onAutoRefreshChange: (mode: TableAutoRefreshMode) => void;
}) => {
    const intl = useIntl();
    const [draggedColumnId, setDraggedColumnId] = useState<string | null>(null);
    const [dragOverColumnId, setDragOverColumnId] = useState<string | null>(null);

    const dragHandleLabel = intl.formatMessage({
        id: 'reactive-table.options.drag-handle',
        description: 'Accessible label for the drag handle used to reorder table columns',
        defaultMessage: 'Drag to reorder column'
    });

    const autoRefreshOptions = useMemo(
        () => [
            {
                value: 'on',
                label: intl.formatMessage({
                    id: 'reactive-table.options.auto-refresh.on',
                    description: 'Table options label for refreshing the list immediately when new rows are created',
                    defaultMessage: 'On'
                })
            },
            {
                value: 'delayed',
                label: intl.formatMessage({
                    id: 'reactive-table.options.auto-refresh.delayed',
                    description: 'Table options label for refreshing the list on create with a short debounce between refreshes',
                    defaultMessage: 'Delayed'
                })
            },
            {
                value: 'manual',
                label: intl.formatMessage({
                    id: 'reactive-table.options.auto-refresh.manual',
                    description: 'Table options label for requiring a manual refresh when new rows are created',
                    defaultMessage: 'Manual'
                })
            }
        ],
        [intl]
    );

    return (
        <div className='p-4'>
            <h2 className='mb-4 text-lg font-semibold'>
                <FormattedMessage
                    id='reactive-table.options.title'
                    description='Title for the table options panel'
                    defaultMessage='Table options'
                />
            </h2>
            <div className='mb-6'>
                <SelectInputV2<TableAutoRefreshMode>
                    id='table-auto-refresh'
                    label={intl.formatMessage({
                        id: 'reactive-table.options.auto-refresh.label',
                        description: 'Label for the create auto-refresh preference in table options',
                        defaultMessage: 'Auto-refresh on create'
                    })}
                    value={props.autoRefresh}
                    options={autoRefreshOptions}
                    isClearable={false}
                    disableSearch
                    onSelect={(option) => {
                        const value = option?.value;
                        if (value === 'on' || value === 'delayed' || value === 'manual') {
                            props.onAutoRefreshChange(value);
                        }
                    }}
                />
            </div>
            <div className='mb-4 flex items-center justify-between'>
                <span className='text-sm text-gray-600'>
                    <FormattedMessage
                        id='reactive-table.options.columns'
                        description='Label for the column visibility and ordering section in the table options panel'
                        defaultMessage='Columns'
                    />
                </span>
                <button
                    onClick={props.onReset}
                    className='text-primary text-sm hover:underline'>
                    <FormattedMessage
                        id='reactive-table.options.reset'
                        description='Button label for resetting table column configuration to the default'
                        defaultMessage='Reset'
                    />
                </button>
            </div>
            <div className='flex grow flex-col justify-between'>
                {props.columns.map((col) => {
                    const isVisible = col.getIsVisible();
                    return (
                        <div
                            key={col.id}
                            draggable
                            onDragStart={(event) => {
                                event.dataTransfer.effectAllowed = 'move';
                                event.dataTransfer.setData('text/plain', col.id);
                                setDraggedColumnId(col.id);
                            }}
                            onDragEnd={() => {
                                setDraggedColumnId(null);
                                setDragOverColumnId(null);
                            }}
                            onDragOver={(event) => {
                                event.preventDefault();
                                event.dataTransfer.dropEffect = 'move';
                                if (dragOverColumnId !== col.id) setDragOverColumnId(col.id);
                            }}
                            onDragLeave={() => {
                                if (dragOverColumnId === col.id) setDragOverColumnId(null);
                            }}
                            onDrop={(event) => {
                                event.preventDefault();
                                const draggedId = event.dataTransfer.getData('text/plain') || draggedColumnId;
                                setDraggedColumnId(null);
                                setDragOverColumnId(null);
                                if (draggedId) props.onReorderColumn(draggedId, col.id);
                            }}
                            className={`mb-2 flex cursor-grab items-center justify-start gap-3 border-l-2 px-2 py-1 active:cursor-grabbing ${
                                dragOverColumnId === col.id ? 'border-brand-primary bg-background-default' : 'border-transparent'
                            } ${draggedColumnId === col.id ? 'opacity-60' : ''}`}>
                            <div
                                aria-label={dragHandleLabel}
                                className='flex w-4 shrink-0 cursor-grab flex-wrap content-center justify-center gap-0.5'
                                role='img'>
                                <span className='bg-brand-muted size-1 rounded-full' />
                                <span className='bg-brand-muted size-1 rounded-full' />
                                <span className='bg-brand-muted size-1 rounded-full' />
                                <span className='bg-brand-muted size-1 rounded-full' />
                                <span className='bg-brand-muted size-1 rounded-full' />
                                <span className='bg-brand-muted size-1 rounded-full' />
                            </div>
                            <div className='min-w-0'>
                                <CheckboxInput
                                    id={col.id}
                                    value={isVisible}
                                    onChange={(value) => col.toggleVisibility(value)}
                                    disabled={!col.getCanHide() || (isVisible && props.visibleColumnCount <= 1)}
                                    label={getColumnOptionsLabel(col)}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
